/**
 * Page 4 — Section 2: NIST CSF 2.0 Functional Ratings.
 *
 * Layout:
 *   • Eyebrow + title + 1-line subtitle
 *   • Function flow row: 6 color-coded pills (Govern → … → Recover)
 *   • 2 × 3 grid of function cards: name, maturity pill, lightweight bar,
 *     short commentary (3–4 lines max)
 *
 * Data sources (no hardcoding):
 *   • Maturity & controls_evaluated → calculate_nist_function_scores RPC
 *     (already fetched in generateReviewerReport.ts)
 *   • Commentary text → nistFunctionNarrative() — the SAME helper that
 *     drives the live "NIST CSF Function Commentary" panel on the
 *     Executive Summary (Live) tab. Band-aware, deterministic.
 */
import type jsPDF from "jspdf";
import { PAGE_MARGIN, REPORT_THEME, type RGB } from "./theme";
import { drawRunningHeader, drawPageFooter, drawSectionTitle } from "./pageChrome";
import {
  bandFromScore,
  nistFunctionNarrative,
  type MaturityBand,
} from "@/reports/telecom/domainNarratives";

export interface NistFunctionScoreRow {
  function_name: string;
  average_score: number | null;
  controls_evaluated?: number | null;
}

export interface NistFunctionRatingsContext {
  scores: NistFunctionScoreRow[];
  clientOrgName: string;
  methodology?: string | null;
  frameworks?: string[] | null;
  pageNumber?: number;
  /** Pre-built priority focus statements (top 3–4). */
  priorityFocusAreas?: string[];
}

const FUNCTION_ORDER = ["Govern", "Identify", "Protect", "Detect", "Respond", "Recover"] as const;

/** NIST CSF function pill colors (matches platform NIST color standard). */
const FUNCTION_COLORS: Record<(typeof FUNCTION_ORDER)[number], { rgb: RGB }> = {
  Govern:   { rgb: [109, 40, 217] },   // violet-700
  Identify: { rgb: [2, 132, 199] },    // sky-600
  Protect:  { rgb: [5, 150, 105] },    // emerald-600
  Detect:   { rgb: [217, 119, 6] },    // amber-600
  Respond:  { rgb: [225, 29, 72] },    // rose-600
  Recover:  { rgb: [79, 70, 229] },    // indigo-600
};

/** Maturity band → color (per spec: Minimal=Red, Adequate=Orange, Strong=Green, Extensive=Blue). */
const BAND_COLORS: Record<MaturityBand, { rgb: RGB }> = {
  Minimal:   { rgb: [220, 38, 38] },   // red-600
  Adequate:  { rgb: [245, 158, 11] },  // amber-500
  Strong:    { rgb: [22, 163, 74] },   // green-600
  Extensive: { rgb: [37, 99, 235] },   // blue-600
  Unrated:   { rgb: [148, 163, 184] }, // slate-400
};

const BAND_FILL_LEVEL: Record<MaturityBand, number> = {
  Unrated: 0,
  Minimal: 0.25,
  Adequate: 0.5,
  Strong: 0.75,
  Extensive: 1,
};

export function renderNistFunctionRatings(doc: jsPDF, ctx: NistFunctionRatingsContext): void {
  doc.addPage();
  const pageW = doc.internal.pageSize.getWidth();

  drawRunningHeader(doc, {
    clientName: ctx.clientOrgName,
    frameworks: ctx.frameworks ?? null,
    methodology: ctx.methodology ?? null,
  });

  let y = drawSectionTitle(doc, {
    sectionNumber: "03",
    title: "Cyber Resilience & Domain Ratings – NIST CSF 2.0",
  });

  // ── Subtitle (1–2 lines max) ──
  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  const orgLabel = (ctx.clientOrgName ?? "").trim() || "the organization";
  const subtitle =
    "The NIST Cybersecurity Framework (CSF) 2.0 Functional Ratings provide a view of cybersecurity maturity across the six core functions: Govern, Identify, Protect, Detect, Respond, and Recover. Together, these functions illustrate how effectively the organization manages cyber risk, protects critical assets, detects threats, responds to incidents, and restores operations following disruption.";
  const subLines = doc.splitTextToSize(subtitle, pageW - PAGE_MARGIN * 2);
  doc.text(subLines, PAGE_MARGIN, y, { lineHeightFactor: 1.35 } as any);
  y += subLines.length * 13 + 14;

  // (Function flow row removed — it competed with card colors and obscured the
  // maturity rating as the page's focal point. The 2×3 card grid below already
  // names each function.)
  y += 4;

  // ── 2 × 3 grid of function cards ──
  const scoreMap = new Map(
    (ctx.scores ?? []).map((s) => [s.function_name, s] as const),
  );

  const gridTop = y + 10;
  const gridLeft = PAGE_MARGIN;
  const gridRight = pageW - PAGE_MARGIN;
  const cols = 3;
  const rows = 2;
  const colGap = 12;
  const rowGap = 12;
  const cardW = (gridRight - gridLeft - colGap * (cols - 1)) / cols;
  const cardH = 150;

  FUNCTION_ORDER.forEach((fnName, idx) => {
    const r = Math.floor(idx / cols);
    const c = idx % cols;
    const cardX = gridLeft + c * (cardW + colGap);
    const cardY = gridTop + r * (cardH + rowGap);

    const row = scoreMap.get(fnName);
    const score = row?.average_score ?? null;
    const controls = Number(row?.controls_evaluated ?? 0);
    const band = bandFromScore(score, controls);
    const commentary = nistFunctionNarrative(fnName, score, controls);

    drawFunctionCard(doc, {
      x: cardX,
      y: cardY,
      w: cardW,
      h: cardH,
      functionName: fnName,
      band,
      commentary,
    });
  });

  // ── Priority Focus Areas (no faint divider lines — cleaner whitespace) ──
  const gridBottom = gridTop + rows * cardH + (rows - 1) * rowGap;

  drawPriorityFocusAreas(doc, {
    y: gridBottom + 42,
    pageW,
    items: ctx.priorityFocusAreas ?? [],
  });

  drawPageFooter(doc, ctx.pageNumber ?? 4);
}

function drawFunctionFlowRow(doc: jsPDF, y: number, pageW: number): void {
  const inner = pageW - PAGE_MARGIN * 2;
  const gap = 8;
  const pillH = 26;
  const pillW = (inner - gap * (FUNCTION_ORDER.length - 1)) / FUNCTION_ORDER.length;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);

  FUNCTION_ORDER.forEach((fn, i) => {
    const x = PAGE_MARGIN + i * (pillW + gap);
    const c = FUNCTION_COLORS[fn].rgb;
    doc.setFillColor(...c);
    doc.roundedRect(x, y, pillW, pillH, pillH / 2, pillH / 2, "F");

    doc.setTextColor(255, 255, 255);
    const label = fn.toUpperCase();
    const tw = doc.getTextWidth(label);
    doc.text(label, x + pillW / 2 - tw / 2, y + pillH / 2 + 3.5);
  });
}

function drawFunctionCard(
  doc: jsPDF,
  args: {
    x: number;
    y: number;
    w: number;
    h: number;
    functionName: string;
    band: MaturityBand;
    commentary: string;
  },
): void {
  const { x, y, w, h, functionName, band, commentary } = args;
  const fnColor = FUNCTION_COLORS[functionName as (typeof FUNCTION_ORDER)[number]].rgb;
  const bandColor = BAND_COLORS[band].rgb;

  // Card background + border
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...REPORT_THEME.border.rgb);
  doc.setLineWidth(0.8);
  doc.roundedRect(x, y, w, h, 6, 6, "FD");

  // Top accent stripe in function color
  doc.setFillColor(...fnColor);
  doc.rect(x, y, w, 4, "F");

  const padX = 14;
  const innerW = w - padX * 2;

  // Function name
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(functionName.toUpperCase(), x + padX, y + 24);

  // Maturity pill (band)
  const pillLabel = band === "Unrated" ? "Not Assessed" : band;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  const pillTextW = doc.getTextWidth(pillLabel);
  const pillPadX = 8;
  const pillH = 16;
  const pillW = pillTextW + pillPadX * 2;
  const pillY = y + 34;
  doc.setFillColor(...bandColor);
  doc.roundedRect(x + padX, pillY, pillW, pillH, pillH / 2, pillH / 2, "F");
  doc.setTextColor(255, 255, 255);
  doc.text(pillLabel, x + padX + pillPadX, pillY + pillH / 2 + 3);

  // Lightweight maturity bar (4 segments, filled to band level)
  const barTop = pillY + pillH + 14;
  const barH = 6;
  const barLeft = x + padX;
  const barW = innerW;
  const segCount = 4;
  const segGap = 3;
  const segW = (barW - segGap * (segCount - 1)) / segCount;
  const filledSegs = Math.round(BAND_FILL_LEVEL[band] * segCount);

  for (let i = 0; i < segCount; i++) {
    const segX = barLeft + i * (segW + segGap);
    if (i < filledSegs) {
      doc.setFillColor(...bandColor);
    } else {
      doc.setFillColor(...REPORT_THEME.surfaceAlt.rgb);
    }
    doc.roundedRect(segX, barTop, segW, barH, barH / 2, barH / 2, "F");
  }

  // Commentary (clamped to ~4 lines)
  const commentaryY = barTop + barH + 16;
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.8);
  const lines = doc.splitTextToSize(commentary, innerW) as string[];
  const maxLines = 5;
  const shown = lines.length > maxLines
    ? [...lines.slice(0, maxLines - 1), lines[maxLines - 1].replace(/\.?\s*$/, "…")]
    : lines;
  doc.text(shown, x + padX, commentaryY, { lineHeightFactor: 1.42 } as any);
}

/**
 * Parse a priority statement produced by buildPriorityFocusAreas.
 * Examples:
 *   "Strengthen Identify — e.g. MFA not enforced…"
 *   "Address material gaps in Protect — controls in this function are currently rated Minimal."
 * Returns the matched function tag (uppercase) and a short action statement.
 */
function parsePriorityStatement(raw: string): { tag: string | null; statement: string } {
  const text = (raw ?? "").trim();
  if (!text) return { tag: null, statement: "" };
  // Try to detect a leading function name.
  let tag: string | null = null;
  for (const fn of FUNCTION_ORDER) {
    const re = new RegExp(`\\b${fn}\\b`, "i");
    if (re.test(text)) { tag = fn.toUpperCase(); break; }
  }
  // Take the portion after an em/en/hyphen dash if present, else use full text.
  const dashSplit = text.split(/\s+[—–-]\s+/);
  let statement = dashSplit.length > 1 ? dashSplit.slice(1).join(" — ") : text;
  // Strip an "e.g." prefix for cleanliness.
  statement = statement.replace(/^e\.g\.\s*/i, "").trim();
  return { tag, statement };
}

function drawPriorityFocusAreas(
  doc: jsPDF,
  args: { y: number; pageW: number; items: string[] },
): void {
  const { y, pageW, items } = args;
  const left = PAGE_MARGIN;
  const right = pageW - PAGE_MARGIN;
  const innerW = right - left;

  // Section title
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("Priority Focus Areas", left, y);

  // Subtle underline accent
  doc.setDrawColor(...REPORT_THEME.gold.rgb);
  doc.setLineWidth(1.2);
  doc.line(left, y + 5, left + 60, y + 5);

  const top3or4 = (items ?? []).filter((s) => !!s?.trim()).slice(0, 4);
  if (top3or4.length === 0) {
    doc.setTextColor(...REPORT_THEME.textMuted.rgb);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9.5);
    doc.text(
      "No priority focus areas identified yet — complete reviewer ratings to populate this section.",
      left,
      y + 22,
    );
    return;
  }

  // Render as horizontal list rows: [TAG] — statement
  // Fixed row height + fixed badge column for balanced, executive layout.
  const ROW_H = 26;
  const BADGE_COL_W = 96;
  const tagPillH = 16;
  const tagPaddingX = 8;
  const statementFontSize = 10;
  let cursorY = y + 24;

  top3or4.forEach((raw) => {
    const { tag, statement } = parsePriorityStatement(raw);
    const fnKey = (FUNCTION_ORDER.find((f) => f.toUpperCase() === tag) ?? null);
    const tagColor = fnKey ? FUNCTION_COLORS[fnKey].rgb : REPORT_THEME.navy.rgb;

    // Badge — outlined pill in fixed left column
    if (tag) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      const tagW = doc.getTextWidth(tag) + tagPaddingX * 2;
      doc.setDrawColor(...tagColor);
      doc.setLineWidth(0.8);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(left, cursorY - 4, tagW, tagPillH, tagPillH / 2, tagPillH / 2, "FD");
      doc.setTextColor(...tagColor);
      doc.text(tag, left + tagPaddingX, cursorY + 7);
    }

    // Statement — single line, ellipsised, vertically aligned to badge
    const textX = left + BADGE_COL_W;
    const statementMaxW = right - textX;
    doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(statementFontSize);
    const split = doc.splitTextToSize(statement, statementMaxW) as string[];
    const shown = split.length > 1
      ? (split[0] ?? "").replace(/\.?\s*$/, "…")
      : (split[0] ?? "");
    doc.text(shown, textX, cursorY + 7);

    cursorY += ROW_H;
  });
}

